import { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay, Pagination } from 'swiper/modules';
import Slider from "react-slick";
import 'swiper/css';
import 'swiper/css/navigation';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaShoppingCart, FaHeart, FaRegHeart } from "react-icons/fa";
import Footer from './component/Footer';
import { useCart } from './context/CartContext';
import api from "./api";
import CategoryCart from './component/CategoryCart';
import ArrivalCart from './component/ArrivalCart';
import "swiper/css/pagination";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getLocalData } from "./utils/localStorageHelper";
import { useWishlist } from './context/WishlistContext';



const Home = () => {
    const navigate = useNavigate();

    const bestSellerGoToSingle = (items) => {
        navigate(`/product-details/${items._id}`, { state: { items } });
    };

    const { addToCart, updateQuantity, removeFromCart, cart } = useCart();
    const [banner, setBanner] = useState([]);
    const [seller, setSeller] = useState([]);
    const [trending, setTrending] = useState([]);
    const [width, setWidth] = useState(window.innerWidth);

    // ─── Cart Quantities ──────────────────────────────────────────────────────
    // FIX: qty is derived from the real cart (single source of truth).
    // No separate localStorage copy, so removing item in cart page
    // automatically resets qty on Home.
    const cartQuantities = useMemo(() => {
        const map = {};
        cart.forEach((item) => {
            const id = item.id || item._id || item.productId;
            if (id && map[id] === undefined) map[id] = item.quantity || 1;
        });
        return map;
    }, [cart]);

    const cartQuantitiesRef = useRef(cartQuantities);
    cartQuantitiesRef.current = cartQuantities;

    // no-op: old handlers still call it, qty now come from cart
    const updateCartQty = () => { };
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Wishlist State ───────────────────────────────────────────────────────
    const [wishlistedProducts, setWishlistedProducts] = useState({});
    const [wishlist, setWishlist] = useState([]);
    const { wishlistCount, setWishlistCount } = useWishlist();

    // Fetch existing wishlist from API on mount so heart icons show correct state
    useEffect(() => {
        const fetchWishlist = async () => {
            console.log("wishlist1234-1")
            const storedData = localStorage.getItem("userData");
            const token = storedData ? JSON.parse(storedData).token : null;
            if (!token) return; // not logged in — skip
            try {
                const res = await api.get("/user/get-wishlists", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                // API returns wishlist array — shape: [{ _id, ... }, ...]
                // Support both res.data.data and res.data.wishlist depending on backend
                console.log("wishlist1234-2")
                const items =
                    res.data?.data ||
                    res.data?.wishlist ||
                    res.data?.products ||
                    [];
                if (Array.isArray(items)) {
                    const map = {};
                    items.forEach((item) => {
                        // item may be the product itself or a wrapper with .product
                        const id = item._id || item?.product?._id;
                        if (id) map[id] = true;
                    });
                    console.log("wishlist1234", map)
                    setWishlistedProducts(map);
                    setWishlist(items);
                    setWishlistCount(items.length);
                }
            } catch (err) {
                console.log("Failed to fetch wishlist", err);
            }
        };
        fetchWishlist();
    }, []);
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Add / Remove Wishlist (API-backed) ──────────────────────────────────
    const addWishList = async (product) => {
        console.log("itemproduct", product)
        // 1️⃣ Get user token
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;

        // 2️⃣ If user is not logged in → navigate to login page silently
        if (!token) {
            setWishlist([]);
            setWishlistCount(0);
            setWishlistedProducts({});
            navigate("/user-login");
            return;
        }

        // 3️⃣ Check current wishlist status
        const isWishlisted = !!wishlistedProducts[product._id];

        try {
            // 4️⃣ Choose endpoint based on status
            const endpoint = isWishlisted
                ? "/user/add-wishlist"
                : "/user/add-wishlist";

            // 5️⃣ Call API to update DB
            await api.put(
                endpoint,
                { productId: product._id, product },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // 6️⃣ Update UI only after API succeeds
            setWishlistedProducts((prev) => ({
                ...prev,
                [product._id]: !isWishlisted,
            }));

            setWishlistCount((prev) =>
                !isWishlisted ? prev + 1 : Math.max(prev - 1, 0)
            );

            console.log("Wishlist data >>>", isWishlisted);

            if (isWishlisted) {
                setWishlist((prev) => prev.filter((item) => item._id !== product._id));
                toast.info(`Removed from wishlist`, {
                    position: "top-right",
                    autoClose: 2000,
                    theme: "dark",
                });
            } else {
                setWishlist((prev) => [...prev, product]);
                toast.success(`Added to wishlist ❤️`, {
                    position: "top-right",
                    autoClose: 2000,
                    theme: "dark",
                });
            }
        } catch (err) {
            console.error("Error updating wishlist:", err);
            toast.error("Failed to update wishlist. Please try again.", {
                position: "top-right",
                autoClose: 3000,
                theme: "dark",
            });
        }
    };
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // STEP 2: When cart context loads (async), seed any products we don't
    // already know about. NEVER overwrite keys already in cartQuantitiesRef
    // — that protects optimistic updates from being wiped.
    const cartSeedDone = useRef(false);
    useEffect(() => {
        if (cart.length === 0) return;
        updateCartQty((prev) => {
            const next = { ...prev };
            // Add any cart items not yet in our local map
            cart.forEach((item) => {
                const id = item.id || item._id || item.productId;
                if (id && next[id] === undefined) {
                    next[id] = item.quantity || 1;
                }
            });
            // Remove keys for products no longer in cart
            const cartIdSet = new Set(cart.map((i) => i.id || i._id || i.productId));
            Object.keys(next).forEach((id) => {
                if (!cartIdSet.has(id)) delete next[id];
            });
            return next;
        });
        cartSeedDone.current = true;
    }, [cart]);

    const settings = {
        dots: true,
        infinite: true,
        autoplay: true,
        autoplaySpeed: 3000,
        pauseOnHover: false,
        pauseOnFocus: false,
        slidesToShow: width < 768 ? 1 : width < 1024 ? 2 : 3,
        slidesToScroll: 1,
        arrows: true,
        speed: 800,
        cssEase: "ease-in-out",
    };

    useEffect(() => {
        const fetchSections = async () => {
            try {
                const res = await api.get("/website/home_page");
                setBanner(res.data.data.banners);
                setSeller(res.data.data.BestSeller);
                setTrending(res.data.data.Trending);
            } catch (err) {
                console.log("Failed to fetch section", err);
            }
        };
        fetchSections();
    }, []);

    const [isVisible, setIsVisible] = useState(false);
    const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setIsHamburgerOpen(false);
            }
        };
        if (isHamburgerOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
        } else {
            document.removeEventListener('mousedown', handleOutsideClick);
        }
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isHamburgerOpen]);

    useEffect(() => {
        const handleScroll = () => {
            setIsVisible(window.scrollY > 300);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const nextRef = useRef(null);
    const prevRef = useRef(null);

    const [quantity, setQuantity] = useState(1);
    const [selectedCategory, setSelectedCategory] = useState('1');
    const handleCategoryChange = (event) => {
        setSelectedCategory(event.target.value);
        console.log('Selected category:', event.target.value);
    };
    const toggleBoxRef = useRef(null);

    useEffect(() => {
        const el = toggleBoxRef.current;
        if (el) {
            el.classList.add('highlighted');
        }
    }, []);

    const [selectedColor, setSelectedColor] = useState('');
    const [selectedWeight, setSelectedWeight] = useState('');

    const handleColorChange = (event) => {
        setSelectedColor(event.target.value);
    };

    const handleWeightChange = (event) => {
        setSelectedWeight(event.target.value);
    };

    // ─── Handle Add to Cart — sets quantity to 1 and shows controls ──────────
    const handleAddToCart = async (product) => {
        if (!product) return;

        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;

        if (!token) {
            navigate("/user-login");
            return;
        }

        const selectedColor = product?.variants?.[0]?.color?.name;
        const selectedSize = product?.variants?.[0]?.sizes?.[0]?.size;
        const selectedStock = product?.variants?.[0]?.sizes?.[0]?.stock;
        const qty = "1";

        if (selectedStock <= 0) {
            toast.warning("This Size is out of stock. Please Select another Size", {
                position: "top-right",
                autoClose: 2000,
                theme: "dark",
            });
            return;
        }

        await addToCart(product, selectedColor, selectedSize, qty);

        // Immediately show qty controls — persisted via updateCartQty
        updateCartQty((prev) => ({ ...prev, [product._id]: 1 }));

        toast.success(`${qty} Added to cart successfully`, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "dark",
        });
    };

    // ─── Increase quantity ────────────────────────────────────────────────────
    const handleIncreaseQty = async (product) => {
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;

        if (!token) {
            navigate("/user-login");
            return;
        }

        const currentQty = cartQuantitiesRef.current[product._id] || 1;
        const maxStock = product?.variants?.[0]?.sizes?.[0]?.stock || 99;

        if (currentQty >= maxStock) {
            toast.warning("Maximum stock limit reached", {
                position: "top-right",
                autoClose: 2000,
                theme: "dark",
            });
            return;
        }

        console.log("handle Increase Home >>>> ",maxStock);

        const newQty = currentQty + 1;
        // Update ref + state + localStorage immediately — before awaiting API
        updateCartQty((prev) => ({ ...prev, [product._id]: newQty }));
        await updateQuantity(product, product?.variants?.[0]?.color?.name, product?.variants?.[0]?.sizes?.[0]?.size, newQty);
    };

    // ─── Decrease quantity ────────────────────────────────────────────────────
    const handleDecreaseQty = async (product) => {
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;

        if (!token) {
            navigate("/user-login");
            return;
        }

        const currentQty = cartQuantitiesRef.current[product._id] || 1;
        const newQty = currentQty - 1;
        
        if (newQty <= 0) {
            // Remove from cart completely
            const cartItem = cart.find((item) => (item.id || item._id) === product._id);
            
            if (cartItem) {
                await removeFromCart(cartItem, cartItem?.variant?.color?.name, cartItem?.variant?.size);
                console.log("Item Removed >>>", cartItem._id);
            }
            updateCartQty((prev) => {
                const updated = { ...prev };
                delete updated[product._id];
                return updated;
            });
            return;
        }

        // Update ref + state + localStorage immediately — before awaiting API
        updateCartQty((prev) => ({ ...prev, [product._id]: newQty }));
        await updateQuantity(product, product?.variants?.[0]?.color?.name, product?.variants?.[0]?.sizes?.[0]?.size, newQty);
    };

    // ─── Handle Buy Now ───────────────────────────────────────────────────────
    const handleBuyNow = (product) => {
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;

        const selectedColor = product?.variants?.[0]?.color?.name;
        const selectedSize = product?.variants?.[0]?.sizes?.[0]?.size;
        const selectedStock = product?.variants?.[0]?.sizes?.[0]?.stock;
        const quantity = "1";

        if (selectedStock <= 0) {
            toast.warning("This Size is out of stock. Please Select another Size", {
                position: "top-right",
                autoClose: 2000,
                theme: "dark",
            });
            return;
        }

        const selectedVariant = getSelectedVariant(product, selectedColor, selectedSize);
        if (!selectedVariant) {
            console.error("Selected variant not found!");
            return;
        }

        const updatedProduct = {
            ...product,
            variant: selectedVariant,
            quantity: quantity,
        };

        if (!token) {
            navigate("/user-login");
            return;
        }

        navigate("/check-out", {
            state: {
                token,
                products: updatedProduct,
                singleProduct: "yes",
                singleProductId: product._id,
            },
        });
    };

    const getSelectedVariant = (product, selectedColor, selectedSize) => {

        if (!product?.variants) return null;

        // Find the color variant
        const colorVariant = product.variants.find(
            (v) => v.color.name === selectedColor
        );
        // console.log("varient data >>>>>",product.variants);
        // console.log("selectedColor data >>>>>",selectedColor);

        if (!colorVariant) return null;



        // Find the size variant inside that color
        const sizeVariant = colorVariant.sizes.find(
            (s) => s.size === selectedSize
        );
        if (!sizeVariant) return null;

        return {
            color: colorVariant.color,  // {name: 'Black', value: '#000000'}
            size: sizeVariant.size,     // 'M'
            originalPrice: sizeVariant.originalPrice,
            discountedPrice: sizeVariant.discountedPrice,
            stock: sizeVariant.stock,
            images: colorVariant.images, // optional, if needed
        };
    };

    const testimonials = [
        {
            img: "assets/img/other/testimonial-thumb1.png",
            name: "Nike Mardson",
            subtitle: "fashion",
            desc: "Find everything you need — anytime, anywhere. Overay makes online shopping simple, safe, and affordable.",
        },
        {
            img: "assets/img/other/testimonial-thumb2.png",
            name: "Laura Johnson",
            subtitle: "fashion",
            desc: "Join millions of happy shoppers who trust Overay for quality, speed, and unbeatable deals every day.",
        },
        {
            img: "assets/img/other/testimonial-thumb3.png",
            name: "Richard Smith",
            subtitle: "fashion",
            desc: "Join millions of happy shoppers who trust Overay for quality, speed, and unbeatable deals every day.",
        },
        {
            img: "assets/img/other/testimonial-thumb1.png",
            name: "Nike Mardson",
            subtitle: "fashion",
            desc: "Shop smarter with Overay. From fashion to electronics, find everything you love delivered right to your door.",
        },
        {
            img: "assets/img/other/testimonial-thumb2.png",
            name: "Laura Johnson",
            subtitle: "fashion",
            desc: "Join millions of happy shoppers who trust Overay for quality, speed, and unbeatable deals every day.",
        },
        {
            img: "assets/img/other/testimonial-thumb3.png",
            name: "Richard Smith",
            subtitle: "fashion",
            desc: "At Overay, we bring you the best of fashion, lifestyle, and electronics — all with a click. Shop easy. Shop happy.",
        },
    ];



    const renderSellerCard = (items, index) => {
        const allOutOfStock = items.variants?.every(variant =>
            variant.sizes?.every(size => size.stock === 0)
        );

        const selectedStockProduct = items?.variants?.[0]?.sizes?.[0]?.stock;

        const isInCart = cartQuantities.hasOwnProperty(items._id);
        const currentQty = cartQuantities[items._id] || 0;
        const isWishlisted = !!wishlistedProducts[items._id];

        return (
            <SwiperSlide key={`${items._id}-${index}`}>
                <div
                    style={{
                        border: "3px solid #FFD700",
                        borderRadius: "6px",
                        height: "auto",
                        minHeight: "370px",
                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                        display: "flex",
                        flexDirection: "column",
                        background: "#fff",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        overflow: "hidden",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-8px)";
                        e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.2)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)";
                    }}
                >
                    {/* ── Image ── */}
                    <div
                        style={{
                            width: "100%",
                            height: "200px",
                            overflow: "hidden",
                            position: "relative",
                            cursor: "pointer",
                            flexShrink: 0,
                        }}
                        onClick={() => bestSellerGoToSingle(items)}
                    >
                        <img
                            src={items?.variants?.[0]?.images?.[0] || "https://via.placeholder.com/300x300?text=No+Image"}
                            alt={items?.title || "Product"}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center",
                                display: "block",
                            }}
                        />

                        {/* Out of stock badge */}
                        {allOutOfStock && (
                            <span style={{
                                position: "absolute", top: "10px", left: "10px",
                                background: "rgba(255,255,255,0.9)", color: "red",
                                fontWeight: "bold", fontSize: "12px",
                                padding: "3px 8px", borderRadius: "3px",
                            }}>
                                Out of Stock
                            </span>
                        )}

                        {/* Wishlist button */}
                        <button
                            onClick={(e) => { e.stopPropagation(); addWishList(items); }}
                            style={{
                                position: "absolute",
                                top: "10px",
                                right: "10px",
                                background: isWishlisted ? "rgba(237, 46, 46, 0.95)" : "#CEB256",
                                border: "none",
                                borderRadius: "50%",
                                width: "32px",
                                height: "32px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                cursor: "pointer",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                transition: "all 0.3s",
                                zIndex: 10,
                            }}
                        >
                            {isWishlisted ? (
                                <FaHeart style={{ color: "white", fontSize: "16px" }} />
                            ) : (
                                <FaRegHeart style={{ color: "white", fontSize: "16px" }} />
                            )}
                        </button>

                        {/* Rating pill */}
                        <div style={{
                            position: "absolute", bottom: "8px", left: "8px",
                            background: "rgba(0,0,0,0.6)", color: "white",
                            padding: "3px 6px", borderRadius: "4px", fontSize: "11px",
                            display: "flex", alignItems: "center", gap: "3px",
                        }}>
                            ⭐ {items.averageRating?.toFixed(1) || "0.0"}
                        </div>
                    </div>

                    {/* ── Body ── */}
                    <div style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        padding: "10px 8px",
                    }}>
                        <div style={{ flex: 1, marginBottom: "8px" }}>

                            {/* Name */}
                            <span style={{
                                display: "block",
                                fontSize: "14px",
                                fontWeight: "700",
                                color: "#333",
                                marginBottom: "4px",
                                lineHeight: "1.3",
                            }}>
                                {items?.name || items?.title}
                            </span>
                            <span
                                style={{
                                    display: "block",
                                    fontSize: "14px",
                                }}
                            >Size : {items.variants?.[0]?.sizes?.[0]?.size}, Color : {items.variants?.[0]?.color?.name}, Stock : {items.variants?.[0]?.sizes?.[0]?.stock}</span>

                            {/* Description */}
                            <p style={{
                                fontSize: "11px",
                                color: "#666",
                                marginBottom: "6px",
                                lineHeight: "1.4",
                            }}>
                                {items.description?.split(" ").length > 4
                                    ? items.description.split(" ").slice(0, 4).join(" ") + "..."
                                    : items.description}
                            </p>

                            {/* Prices */}
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "5px",
                                marginBottom: "8px",
                            }}>
                                {(() => {
                                    const original = Number(items.variants?.[0]?.sizes?.[0]?.originalPrice || 0);
                                    const discounted = Number(items.variants?.[0]?.sizes?.[0]?.discountedPrice || 0);
                                    const apiDiscount = items.variants?.[0]?.sizes?.[0]?.discount;

                                    const hasRealDiscount = discounted && discounted < original;

                                    // Use API discount if available, otherwise calculate it
                                    const discountPercent = apiDiscount
                                        ? apiDiscount
                                        : hasRealDiscount
                                            ? Math.round(((original - discounted) / original) * 100)
                                            : 0;

                                    return hasRealDiscount ? (
                                        <>
                                            <span style={{ color: "#28a745", fontWeight: "700", fontSize: "15px" }}>
                                                ₹{discounted}
                                            </span>
                                            <span style={{ color: "#999", fontSize: "13px", textDecoration: "line-through" }}>
                                                ₹{original}
                                            </span>
                                            {discountPercent > 0 && (
                                                <span style={{ color: "black", padding: "2px 6px", borderRadius: "3px", fontSize: "10px", fontWeight: "600" }}>
                                                    {discountPercent}% OFF
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <span style={{ color: "#28a745", fontWeight: "700", fontSize: "15px" }}>
                                            ₹{original}
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* ── Action Buttons ── */}
                        <div style={{ marginTop: "auto", paddingTop: "8px" }}>
                            {isInCart && !allOutOfStock && (selectedStockProduct > 0) ? (
                                <div style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "8px",
                                    width: "100%",
                                    alignItems: "center",
                                }}>
                                    {/* Quantity controls */}
                                    <div style={{
                                        flex: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        border: "2px solid #CEB256",
                                        borderRadius: "5px",
                                        overflow: "hidden",
                                        height: "34px",
                                    }} className="seller-card__qty-wrap">
                                        <button
                                            className="seller-card__qty-btn"
                                            onClick={(e) => { e.stopPropagation(); handleDecreaseQty(items); }}
                                        >−</button>
                                        <span className="seller-card__qty-display">{currentQty}</span>
                                        <button
                                            className="seller-card__qty-btn"
                                            onClick={(e) => { e.stopPropagation(); handleIncreaseQty(items); }}
                                        >+</button>
                                    </div>

                                    {/* Buy Now */}
                                    <button
                                        className="seller-card__btn"
                                        onClick={(e) => { e.stopPropagation(); handleBuyNow(items); }}
                                        style={{
                                            flex: 1,
                                            backgroundColor: "#28a745",
                                            padding: "8px",
                                            borderRadius: "5px",
                                            color: "#fff",
                                            border: "none",
                                            whiteSpace: "nowrap",
                                            fontSize: "11px",
                                            fontWeight: "600",
                                            cursor: "pointer",
                                            height: "34px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        Buy Now
                                    </button>
                                </div>
                            ) : (
                                <div style={{ display: "flex", gap: "6px", width: "100%" }}>
                                    {/* Add to Cart */}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleAddToCart(items); }}
                                        disabled={allOutOfStock}
                                        style={{
                                            flex: 1,
                                            padding: "8px 4px",
                                            backgroundColor: allOutOfStock ? "#ccc" : "#CEB256",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "5px",
                                            fontSize: "11px",
                                            fontWeight: "600",
                                            cursor: allOutOfStock ? "not-allowed" : "pointer",
                                            transition: "all 0.3s ease",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        Add to Cart
                                    </button>

                                    {/* Buy Now */}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleBuyNow(items); }}
                                        disabled={allOutOfStock}
                                        style={{
                                            flex: 1,
                                            padding: "8px 4px",
                                            backgroundColor: allOutOfStock ? "#ccc" : "#28a745",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "5px",
                                            fontSize: "11px",
                                            fontWeight: "600",
                                            cursor: allOutOfStock ? "not-allowed" : "pointer",
                                            transition: "all 0.3s ease",
                                            whiteSpace: "nowrap",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        Buy Now
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Shared qty/btn styles needed by seller-card__qty-wrap classes */}
                <style>{`
                .seller-card__qty-wrap {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    border: 2px solid #CEB256;
                    border-radius: 5px;
                    overflow: hidden;
                    height: 34px;
                    min-width: 0;
                }
                .seller-card__qty-btn {
                    flex: 1;
                    background: #CEB256;
                    color: white;
                    border: none;
                    font-size: 16px;
                    font-weight: 700;
                    cursor: pointer;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                    transition: background 0.2s ease;
                    min-width: 28px;
                }
                .seller-card__qty-btn:hover { background: #b8982a; }
                .seller-card__qty-display {
                    flex: 1;
                    text-align: center;
                    font-size: 13px;
                    font-weight: 700;
                    color: #333;
                    background: #fff;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 24px;
                }
                @media (max-width: 768px) {
                    .seller-card__qty-wrap { height: 30px; }
                    .seller-card__qty-btn  { font-size: 14px; min-width: 24px; }
                    .seller-card__qty-display { font-size: 12px; }
                }
            `}</style>
            </SwiperSlide>
        );
    };
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <>
            {/* Toast Container */}
            {/* <ToastContainer /> */}

            {/* Global responsive fix */}
            <style>{`
                html, body, #root {
                    overflow-x: hidden;
                    max-width: 100%;
                }
                
                .container-fluid {
                    padding-left: 15px !important;
                    padding-right: 15px !important;
                }
                .row {
                    margin-left: 0 !important;
                    margin-right: 0 !important;
                }
                .banner__image {
                    width: 100%;
                    height: auto;
                    display: block;
                    object-fit: cover;
                }
                .hero__slider--section, .hero__slider--inner {
                    width: 100%;
                    overflow: hidden;
                }
                .banner__section img,
                .deals__banner--content img,
                .banner__items img {
                    width: 100%;
                    height: auto;
                    display: block;
                }
                @media (max-width: 768px) {
                    .col-md-4 {
                        padding-left: 8px !important;
                        padding-right: 8px !important;
                    }
                    .section--padding {
                        padding-left: 0 !important;
                        padding-right: 0 !important;
                    }
                }
            `}</style>

            <main className="main__content_wrapper" style={{ overflowX: "hidden", width: "100%", maxWidth: "100vw" }}>

                {/* <!-- Start slider section --> */}
                <section className="hero__slider--section" style={{ overflowX: "hidden", width: "100%" }}>
                    <div className="hero__slider--inner" style={{ width: "100%" }}>
                        <Slider {...settings}>
                            {banner.map((item, index) => (
                                <div key={item.id || index} className="banner__item">
                                    <div className="banner__card">
                                        <img
                                            src={item.image}
                                            alt={`banner-${index}`}
                                            className="banner__image"
                                        />
                                        <div className="banner__content">
                                            <h2 className="banner__title">{item.title}</h2>
                                            <p className="banner__subtitle">{item.subtitle}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </div>
                </section>

                <CategoryCart />
                <ArrivalCart />

                {/* <!-- Start deals banner section --> */}
                <section className="section--padding pt-5" style={{ overflowX: "hidden", width: "100%" }}>
                    <div className="container-fluid" style={{ paddingLeft: "15px", paddingRight: "15px" }}>
                        <div className="">
                            <div className="row">
                                <div className="col-md-4">
                                    <div className="deals__banner--content position__relative corner-border">
                                        <img src="assets/img/pst7.webp" className="img-fluid" />
                                        <div className="overlay-content">
                                            <h4>New Collections</h4>
                                            <Link to="/new-rivals?type=isNewCollection" className="overlay-btn">Explore Now</Link>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="deals__banner--content position__relative corner-border">
                                        <img src="assets/img/pst6.webp" className="img-fluid" id="newarrival" />
                                        <div className="overlay-content">
                                            <h4>Best Sellers</h4>
                                            <Link to="/new-rivals?type=isBestSeller" id='#bestsellers' className="overlay-btn">Explore Now</Link>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="deals__banner--content position__relative corner-border">
                                        <img src="assets/img/pst5.webp" className="img-fluid" />
                                        <div className="overlay-content">
                                            <h4>Trending</h4>
                                            <Link to="/new-rivals?type=isTrending" className="overlay-btn">Explore Now</Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* BEST SELLER SECTION */}
                <section className="product__section section--padding pt-0" style={{
                    position: "relative",
                    marginTop: "40px",
                    padding: "10px",
                    color: "#000",
                    overflow: "hidden",
                    width: "100%",
                    backgroundImage: `
                        linear-gradient(to bottom, rgba(255, 255, 255, 0.15) 0%, rgba(255,255,255,0.87) 50%, rgba(255,255,255,1) 100%),
                        url('/assets/img/ovaraybg.jpg')
                    `,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(20px)"
                }}>
                    <div className="container-fluid mt-4">

                        {/* Header */}
                        <div className="row align-items-center mb-4">
                            <div className="col-md-4 col-12"></div>
                            <div className="col-md-4 col-12 text-center">
                                <div className="banner-wrapper" style={{ textAlign: "center", padding: "20px", position: "relative", zIndex: 1 }}>
                                    <div className="banner-container" style={{
                                        display: "inline-block", background: "#fff", padding: "10px 30px",
                                        border: "2.7px solid #e0b200", borderTopRightRadius: "25px",
                                        borderBottomLeftRadius: "25px", borderTopLeftRadius: "0",
                                        borderBottomRightRadius: "0", position: "relative",
                                        boxShadow: "0 0 15px rgba(255, 223, 100, 0.6)", textAlign: "center",
                                    }}>
                                        <span className="--maintitle banner-text" style={{ fontWeight: "bold", color: "#000", fontSize: "18px", letterSpacing: "1px" }}>
                                            BEST SELLER
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-4 d-flex justify-content-center justify-content-md-end align-items-center mt-3 mt-md-0">
                                <Link to="/new-rivals?type=isBestSeller" className="heartbeat-link" style={{ textDecoration: "none", color: "#ED2E2E", fontWeight: "700", fontSize: "16px", whiteSpace: "nowrap" }}>
                                    Explore All →
                                </Link>
                            </div>
                        </div>

                        {/* Swiper */}
                        {seller.length > 0 ? (
                            <Swiper
                                modules={[Navigation, Pagination, Autoplay]}
                                spaceBetween={12}
                                slidesPerView={4}
                                navigation
                                loop
                                autoplay={{ delay: 80000, disableOnInteraction: true }}
                                breakpoints={{
                                    0: { slidesPerView: 1.08, slidesPerGroup: 1, spaceBetween: 10 },
                                    480: { slidesPerView: 1.5, slidesPerGroup: 1, spaceBetween: 10 },
                                    640: { slidesPerView: 2, slidesPerGroup: 1, spaceBetween: 15 },
                                    768: { slidesPerView: 3, slidesPerGroup: 1, spaceBetween: 15 },
                                    1024: { slidesPerView: 4, slidesPerGroup: 1, spaceBetween: 20 },
                                    1280: { slidesPerView: 5, slidesPerGroup: 1, spaceBetween: 20 },
                                }}
                                className="seller-slider product__items"
                            >
                                {seller.map((items, index) => renderSellerCard(items, index))}
                            </Swiper>
                        ) : (
                            <div className="no-products text-center py-5">
                                <img src="https://via.placeholder.com/200x200?text=No+Products" alt="No sellers" style={{ margin: "0 auto", display: "block", borderRadius: "8px" }} />
                                <p style={{ marginTop: "10px", color: "#666" }}>No sellers found</p>
                            </div>
                        )}

                    </div>
                </section>

                {/* DEALS SECTION */}
                <section className="product__section section--padding pt-0 mt-5" style={{
                    position: "relative",
                    marginTop: "40px",
                    padding: "10px",
                    color: "#000",
                    overflow: "hidden",
                    width: "100%",
                    backgroundImage: `
                        linear-gradient(to bottom, rgba(255, 255, 255, 0.15) 0%, rgba(255,255,255,0.87) 50%, rgba(255,255,255,1) 100%),
                        url('/assets/img/ovaraybg.jpg')
                    `,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(20px)"
                }}>
                    <div className="container-fluid">
                        <div className="banner-wrapper">
                            <div className="banner-container" style={{
                                display: "inline-block", background: "#fff", padding: "10px 30px",
                                border: "2.7px solid #e0b200", borderTopRightRadius: "25px",
                                borderBottomLeftRadius: "25px", borderTopLeftRadius: "0",
                                borderBottomRightRadius: "0", position: "relative",
                                boxShadow: "0 0 15px rgba(255, 223, 100, 0.6)", textAlign: "center",
                            }}>
                                <span className="--maintitle banner-text" style={{ fontWeight: "bold", color: "#000", fontSize: "18px", letterSpacing: "1px" }}>
                                    DEALS
                                </span>
                            </div>
                        </div>

                        <div className="row g-0">
                            {[
                                { price: 399, img: "assets/img/103.png" },
                                { price: 599, img: "assets/img/product/big-product2.jpg" },
                                { price: 899, img: "assets/img/105.png" },
                                { price: 1299, img: "assets/img/product/big-product4.jpg" },
                                { price: 1599, img: "assets/img/106.png" },
                                { price: 1999, img: "assets/img/product/product3.png" },
                                { price: 2499, img: "assets/img/115.png" },
                                { price: 2999, img: "assets/img/product/product1.png" },
                            ].map((item, index) => (
                                <div key={index} className="col-lg-3 col-md-4 col-sm-6 p-0">
                                    <Link to={`/new-collection?price=${item.price}`} style={{ textDecoration: "none" }}>
                                        <div
                                            className="card text-center"
                                            style={{
                                                borderRadius: "0px", overflow: "hidden", position: "relative",
                                                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                                                transition: "transform 0.3s ease, box-shadow 0.3s ease", cursor: "pointer",
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-5px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.2)"; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 10px rgba(0, 0, 0, 0.1)"; }}
                                        >
                                            <img src={item.img} alt={`Product ₹${item.price}`} className="card-img-top" style={{ objectFit: "cover", height: "450px", width: "100%", display: "block" }} />
                                            <div className="card-body" style={{ position: "absolute", bottom: "10px", left: "0", right: "0", background: "rgba(0,0,0,0.5)", color: "#fff", padding: "0", fontSize: "large", fontWeight: "bold" }}>
                                                Grab Deals Under ₹{item.price}
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* TRENDING NOW SECTION */}
                <section className="product__section section--padding pt-0" style={{
                    position: "relative",
                    marginTop: "40px",
                    padding: "10px",
                    color: "#000",
                    overflow: "hidden",
                    width: "100%",
                    backgroundImage: `
                        linear-gradient(to bottom, rgba(255, 255, 255, 0.15) 0%, rgba(255,255,255,0.87) 50%, rgba(255,255,255,1) 100%),
                        url('/assets/img/ovaraybg.jpg')
                    `,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(20px)"
                }}>
                    <div className="container-fluid mt-4">

                        {/* Header */}
                        <div className="row align-items-center mb-4">
                            <div className="col-md-4 col-12"></div>
                            <div className="col-md-4 col-12 text-center">
                                <div className="banner-wrapper" style={{ textAlign: "center", padding: "20px", position: "relative", zIndex: 1 }}>
                                    <div className="banner-container" style={{
                                        display: "inline-block", background: "#fff", padding: "10px 30px",
                                        border: "2.7px solid #e0b200", borderTopRightRadius: "25px",
                                        borderBottomLeftRadius: "25px", borderTopLeftRadius: "0",
                                        borderBottomRightRadius: "0", position: "relative",
                                        boxShadow: "0 0 15px rgba(255, 223, 100, 0.6)", textAlign: "center",
                                    }}>
                                        <span className="--maintitle banner-text" style={{ fontWeight: "bold", color: "#000", fontSize: "18px", letterSpacing: "1px" }}>
                                            TRENDING NOW
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-4 d-flex justify-content-center justify-content-md-end align-items-center mt-3 mt-md-0">
                                <Link to="/new-rivals?type=isTrending" className="heartbeat-link" style={{ textDecoration: "none", color: "#ED2E2E", fontWeight: "700", fontSize: "16px", whiteSpace: "nowrap" }}>
                                    Explore All →
                                </Link>
                            </div>
                        </div>

                        {/* Swiper */}
                        {trending.length > 0 ? (
                            <Swiper
                                modules={[Navigation, Pagination, Autoplay]}
                                spaceBetween={12}
                                slidesPerView={4}
                                navigation
                                loop
                                autoplay={{ delay: 8000, disableOnInteraction: true }}
                                breakpoints={{
                                    0: { slidesPerView: 1.08, slidesPerGroup: 1, spaceBetween: 10 },
                                    480: { slidesPerView: 1.5, slidesPerGroup: 1, spaceBetween: 10 },
                                    640: { slidesPerView: 2, slidesPerGroup: 1, spaceBetween: 15 },
                                    768: { slidesPerView: 3, slidesPerGroup: 1, spaceBetween: 15 },
                                    1024: { slidesPerView: 4, slidesPerGroup: 1, spaceBetween: 20 },
                                    1280: { slidesPerView: 5, slidesPerGroup: 1, spaceBetween: 20 },
                                }}
                                className="seller-slider product__items"
                            >
                                {trending.map((items, index) => renderSellerCard(items, index))}
                            </Swiper>
                        ) : (
                            <div className="no-products text-center py-5">
                                <img src="https://via.placeholder.com/200x200?text=No+Products" alt="No sellers" style={{ margin: "0 auto", display: "block", borderRadius: "8px" }} />
                                <p style={{ marginTop: "10px", color: "#666" }}>No sellers found</p>
                            </div>
                        )}

                    </div>
                </section>

                {/* Banner Section */}
                <section className="banner__section section--padding pt-0 mt-5" style={{ overflowX: "hidden", width: "100%" }}>
                    <div className="container-fluid" style={{ paddingLeft: "15px", paddingRight: "15px" }}>
                        <div className="row row-cols-md-2 row-cols-1 mb--n28">
                            <div className="col mb-28">
                                <div className="banner__items position__relative">
                                    <img src="assets/img/nj1.webp" />
                                </div>
                            </div>
                            <div className="col mb-28">
                                <div className="banner__items position__relative">
                                    <img src="assets/img/nj2.webp" />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Testimonial Section */}
                <section className="testimonial__section section--padding pt-0" style={{ position: "relative", overflow: "hidden", backgroundColor: "#fff", padding: "100px 0" }}>
                    <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", backgroundImage: `url('/assets/img/ovaraybg12345.png')`, backgroundSize: "cover", backgroundRepeat: "no-repeat", backgroundPosition: "center", zIndex: 0, pointerEvents: "none" }}></div>
                    <div style={{ position: "absolute", top: 0, right: 0, width: "100%", height: "100%", zIndex: 1, pointerEvents: "none" }}></div>

                    <div className="container" style={{ position: "relative", zIndex: 3, padding: '25px', marginTop: '50px', background: 'white', borderRadius: '6px', boxShadow: "1px 2px 4px rgba(0,0,0,0.1), 0 -2px 4px rgba(0,0,0,0.1), 2px 0 4px rgba(0,0,0,0.1), -2px 0 4px rgba(0,0,0,0.1)", border: '2px solid #CEB256' }}>
                        <div style={{ textAlign: "center", marginBottom: "50px", position: "relative", marginTop: '10px' }}>
                            <h2 style={{ fontSize: "28px", fontWeight: "700", color: "#000", textTransform: "uppercase", display: "inline-block", position: "relative", padding: "0 20px" }}>
                                <span style={{ position: "absolute", top: "-10px", left: "50%", transform: "translateX(-50%)", width: "20%", height: "3px", backgroundColor: "#CEB256", display: "block" }}></span>
                                What Clients Say
                                <span style={{ position: "absolute", bottom: "-10px", left: "50%", transform: "translateX(-50%)", width: "20%", height: "3px", backgroundColor: "#CEB256", display: "block" }}></span>
                            </h2>
                        </div>
                        <Swiper
                            modules={[Autoplay, Pagination, Navigation]}
                            spaceBetween={30}
                            slidesPerView={1}
                            loop={true}
                            autoplay={{ delay: 3500, disableOnInteraction: false }}
                            pagination={{ clickable: true }}
                            navigation={true}
                            breakpoints={{ 768: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } }}
                            className="product__swiper seller-slider"
                        >
                            {testimonials.map((item, index) => (
                                <SwiperSlide key={index}>
                                    <div
                                        className="testimonial__card"
                                        style={{ background: "#fff", borderRadius: "10px", padding: "35px 25px", boxShadow: "0 6px 20px rgba(0,0,0,0.08)", textAlign: "center", transition: "transform 0.3s ease, box-shadow 0.3s ease" }}
                                        onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-6px)"; e.currentTarget.style.boxShadow = "0 12px 25px rgba(0,0,0,0.15)"; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.08)"; }}
                                    >
                                        <img src={item.img} alt={item.name} style={{ width: "80px", height: "80px", borderRadius: "50%", objectFit: "cover", marginBottom: "15px" }} />
                                        <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#222", marginBottom: "5px" }}>{item.name}</h3>
                                        <p style={{ color: "#999", fontSize: "14px" }}>{item.subtitle}</p>
                                        <p style={{ color: "#444", fontSize: "15px", marginTop: "10px", lineHeight: "1.6" }}>{item.desc}</p>
                                        <ul className="rating testimonial__rating d-flex justify-content-center" style={{ marginTop: "10px", listStyle: "none", padding: 0 }}>
                                            {[...Array(5)].map((_, star) => (
                                                <li key={star} style={{ color: "#E7B768", margin: "0 2px" }}>★</li>
                                            ))}
                                        </ul>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </section>

                {/* Blog / Delivery section */}
                <section className="blog__section pt-0" style={{ overflowX: "hidden", width: "100%" }}>
                    <div className="container-fluid" style={{ paddingLeft: "15px", paddingRight: "15px" }}>
                        <div className="blog__section--inner blog__swiper--activation swiper">
                            <p>
                                <span style={{ margin: '0px', padding: '0px 0px 10px', border: '0px', fontVariantNumeric: 'inherit', fontVariantEastAsian: 'inherit', fontWeight: 600, fontStretch: 'inherit', fontSize: '14px', lineHeight: 'inherit', fontFamily: 'Lato, sans-serif', verticalAlign: 'baseline', scrollBehavior: 'smooth', display: 'block', color: '#000000', letterSpacing: '1px' }}>
                                    <strong>We Deliver In</strong><br />
                                </span>
                                <span style={{ margin: '0px', padding: '0px', border: '0px', fontVariantNumeric: 'inherit', fontVariantEastAsian: 'inherit', fontStretch: 'inherit', fontSize: '14px', lineHeight: '20px', fontFamily: 'Lato, sans-serif', verticalAlign: 'baseline', scrollBehavior: 'smooth', color: '#000000' }}>
                                    Lucknow | Ludhiana | Mumbai | Nagpur | Navi Mumbai | New Delhi | Jodhpur | Kochi | Kolkata |&nbsp; Noida | Pimpri Chinchwad | Pune | Secunderabad | Surat | Udaipur | Thane | Tirupur | Vadodara | Ahmedabad | Bangalore | Bhopal | Chandigarh | Chennai | Coimbatore | Faridabad | Ghaziabad | Goa | Gurgaon | Hyderabad | Indore | Jaipur &amp; Across India
                                </span>
                            </p>
                        </div>
                    </div>
                </section>

            </main>

            <Footer />

            {/* Quickview Modal */}
            <div className="modal" id="modal1" data-animation="slideInUp">
                <div className="modal-dialog quickview__main--wrapper">
                    <header className="modal-header quickview__header">
                        <button className="close-modal quickview__close--btn" aria-label="close modal" data-close>✕ </button>
                    </header>
                    <div className="quickview__inner">
                        <div className="row row-cols-lg-2 row-cols-md-2">
                            <div className="col">
                                <div className="quickview__product--media product__details--media">
                                    <div className="product__media--preview  swiper">
                                        <div className="swiper-wrapper">
                                            <div className="swiper-slide">
                                                <div className="product__media--preview__items">
                                                    <Link className="product__media--preview__items glightbox" data-gallery="product-media-preview" src="assets/img/product/big-product1.jpg" alt="product-media-img"></Link>
                                                    <div className="product__media--view__icon">
                                                        <Link className="product__media--view__icon--link glightbox" src="assets/img/product/big-product1.jpg" data-gallery="product-media-preview">
                                                            <svg className="product__media--view__icon--svg" xmlns="http://www.w3.org/2000/svg" width="22.51" height="22.443" viewBox="0 0 512 512"><path d="M221.09 64a157.09 157.09 0 10157.09 157.09A157.1 157.1 0 00221.09 64z" fill="none" stroke="currentColor" strokeMiterlimit="10" strokeWidth="32"></path><path fill="none" stroke="currentColor" strokeLinecap="round" strokeMiterlimit="10" strokeWidth="32" d="M338.29 338.29L448 448"></path></svg>
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="product__media--nav swiper">
                                        <div className="swiper-wrapper">
                                            <div className="swiper-slide">
                                                <div className="product__media--nav__items">
                                                    <img className="product__media--nav__items--img" src="assets/img/product/small-product7.png" alt="product-nav-img" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="swiper__nav--btn swiper-button-next"></div>
                                        <div className="swiper__nav--btn swiper-button-prev"></div>
                                    </div>
                                </div>
                            </div>
                            <div className="col">
                                <div className="quickview__info">
                                    <form action="#">
                                        <h2 className="product__details--info__title mb-15">Oversize Cotton Dress</h2>
                                        <div className="product__details--info__price mb-10">
                                            <span className="current__price">$58.00</span>
                                            <span className="old__price">$68.00 </span>
                                        </div>
                                        <div className="quickview__variant--list quantity d-flex align-items-center mb-15">
                                            <div className="quantity__box">
                                                <button type="button" className="quantity__value quickview__value--quantity decrease" aria-label="quantity value" value="Decrease Value">-</button>
                                                <label>
                                                    <input type="number" className="quantity__number quickview__value--number" value={quantity} data-counter onChange={(e) => setQuantity(e.target.value)} />
                                                </label>
                                                <button type="button" className="quantity__value quickview__value--quantity increase" aria-label="quantity value" value="Increase Value">+</button>
                                            </div>
                                            <button className="primary__btn quickview__cart--btn" type="submit">Add To Cart</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scroll top button */}
            <button
                onClick={handleScrollTop}
                style={{
                    position: 'fixed', bottom: '30px', right: '30px', width: '50px', height: '50px',
                    borderRadius: '50%', backgroundColor: '#ceb256', color: '#fff', border: 'none',
                    zIndex: 1000, cursor: 'pointer', display: isVisible ? 'flex' : 'none',
                    alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
                }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 512 512" fill="none" stroke="currentColor" strokeWidth="48" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M112 244l144-144 144 144" />
                    <path d="M256 120v292" />
                </svg>
            </button>
        </>
    )
}

export default Home;